/**
 * Uploading a vendor logo (docs/Decisions.md D74, findings.json FEA-49): the
 * file goes straight to the private staging bucket, is checked and scanned
 * there, and shows on the vendor page only once the scan passes (CR-47).
 * While it scans, this polls the vendor profile; a rejection says why.
 */
import { useEffect, useRef, useState } from 'react';
import * as marketApi from '../api/marketplace';
import { putToPresignedUrl } from '../api/sites';
import { logoFileError } from '../lib/sites';
import { useToast } from './toast';
import { cn, ui } from '../lib/ui';

const LIMITS = { maxBytes: 1024 * 1024, contentTypes: ['image/png', 'image/jpeg', 'image/webp'] };
const POLL_MS = 3000;
const POLL_LIMIT = 40;

export default function VendorLogoUpload({ vendor, onChanged }) {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const polls = useRef(0);
  const pending = vendor.logoPending;
  const scanning = pending?.status === 'scanning';

  // While a scan runs, reload the profile every few seconds until it passes
  // or is rejected (at most POLL_LIMIT times).
  useEffect(() => {
    if (!scanning) {
      polls.current = 0;
      return undefined;
    }
    const timer = setInterval(() => {
      if (polls.current >= POLL_LIMIT) return;
      polls.current += 1;
      onChanged();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [scanning, onChanged]);

  async function upload(e) {
    e.preventDefault();
    const problem = logoFileError(file, LIMITS);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await marketApi.requestVendorLogoUpload(vendor.vendorId, file);
      await putToPresignedUrl(res.uploadUrl, file, file.type);
      await marketApi.confirmVendorLogo(vendor.vendorId, res.logoId);
      setFile(null);
      toast.success('Logo uploaded. It shows on your page once the scan passes.');
      onChanged();
    } catch (err) {
      setError(err.message || 'The logo was not uploaded');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await marketApi.deleteVendorLogo(vendor.vendorId);
      toast.success('Uploaded logo removed');
      onChanged();
    } catch (err) {
      toast.error(err.message || 'Not removed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={upload}
      className={cn(ui.cardFlat, 'mb-4')}
      aria-labelledby="logo-upload-heading"
    >
      <h3 id="logo-upload-heading" className="mt-0 text-title-s">
        Upload a logo
      </h3>
      <p className={cn(ui.hint, 'mt-0')}>
        A PNG, JPEG or WebP image up to 1 MB. It is checked for malware and shows on your page once
        the scan passes; until then members see your image address, if you gave one.
      </p>
      {vendor.logo?.status === 'passed' && (
        <p className={cn(ui.pillSuccess, 'mb-2')}>Uploaded logo in use</p>
      )}
      {scanning && (
        <p className={cn(ui.alertInfo, 'mb-2')} role="status">
          Scanning your logo...
        </p>
      )}
      {pending?.status === 'rejected' && (
        <div className={ui.alertError} role="alert">
          The last upload was not accepted:{' '}
          {(pending.reasons || []).join(' ') || 'it did not pass the scan'}
        </div>
      )}
      <label className={ui.field}>
        <span className={ui.label}>Logo file</span>
        <input
          type="file"
          accept={LIMITS.contentTypes.join(',')}
          className={ui.input}
          onChange={e => {
            setFile(e.target.files?.[0] || null);
            setError(null);
          }}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? 'logo-upload-error' : undefined}
        />
        {error && (
          <span id="logo-upload-error" className={ui.fieldError}>
            {error}
          </span>
        )}
      </label>
      <div className={ui.row}>
        <button
          type="submit"
          className={cn(ui.btnPrimary, ui.btnSm)}
          disabled={busy || !file || scanning}
        >
          {busy ? 'Uploading...' : 'Upload logo'}
        </button>
        {vendor.logo && (
          <button
            type="button"
            className={cn(ui.btnGhost, ui.btnSm)}
            disabled={busy}
            onClick={remove}
          >
            Remove uploaded logo
          </button>
        )}
      </div>
    </form>
  );
}
